import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { airacCycleOn, type NavDataInput, type NavDataState, type NavDataUnit } from '@jupi/shared'
import { api } from '../api/client'
import { formatDate } from '../format'
import { useSnackbar } from '../hooks/useSnackbar'
import { ConfirmDialog } from './ConfirmDialog'
import { useClubToday } from './ExpiryChip'

// The navigation database of an aircraft's GPS units (issue #270): when it was
// loaded, when it runs out, and whether that is a problem. Shared by the
// member app's aircraft page and huolto; whoever holds
// flight.maintenance.write can load a new cycle.

export const navDataKey = (registration: string) => ['aircraft', registration, 'nav-data'] as const

export function useNavData(registration: string | undefined) {
  return useQuery({
    queryKey: navDataKey(registration ?? ''),
    enabled: Boolean(registration),
    queryFn: () => api.get<{ units: NavDataUnit[] }>(`/api/aircraft/${registration}/nav-data`).then((r) => r.units),
  })
}

// Every aircraft's units, one query for the fleet page's chips.
export function useFleetNavData() {
  return useQuery({
    queryKey: ['aircraft', 'nav-data'] as const,
    queryFn: () => api.get<{ units: NavDataUnit[] }>('/api/aircraft/nav-data').then((r) => r.units),
  })
}

const STATE_COLOUR: Record<NavDataState, 'success' | 'warning' | 'error'> = {
  current: 'success',
  expiring: 'warning',
  expired: 'error',
}

const STATE_ICON: Record<NavDataState, string> = {
  current: 'mdi:check-circle-outline',
  expiring: 'mdi:alert-outline',
  expired: 'mdi:alert-octagon-outline',
}

export function NavDataChip({ state }: { state: NavDataState }) {
  const { t } = useTranslation()
  return (
    <Chip size="small" color={STATE_COLOUR[state]} icon={<Icon icon={STATE_ICON[state]} />} label={t(`navData.state_${state}`)} sx={{ fontWeight: 700 }} />
  )
}

const dateLabel = (date: string) => formatDate(`${date}T12:00:00Z`)

export function NavDataCard({
  registration,
  hasGps,
  canEdit,
}: {
  registration: string
  hasGps: boolean
  canEdit: boolean
}) {
  const { t } = useTranslation()
  const { data: units = [] } = useNavData(registration)
  const [editing, setEditing] = useState<NavDataUnit | 'new' | null>(null)
  const [removing, setRemoving] = useState<NavDataUnit | null>(null)
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const remove = useMutation({
    mutationFn: (unit: NavDataUnit) => api.delete(`/api/maintenance/${registration}/nav-data/${unit.navDataId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['aircraft'] })
      showSnackbar(t('navData.removed'))
      setRemoving(null)
    },
    onError: () => showSnackbar(t('common.error'), { severity: 'error' }),
  })

  if (!hasGps && units.length === 0) return null
  const stale = units.filter((u) => u.state === 'expired')

  return (
    <Card>
      <CardContent>
        <Stack direction="row" sx={{ alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" component="h3" sx={{ flex: 1 }}>
            {t('navData.title')}
          </Typography>
          {canEdit ? (
            <Button size="small" startIcon={<Icon icon="mdi:plus" />} onClick={() => setEditing('new')}>
              {t('navData.add')}
            </Button>
          ) : null}
        </Stack>
        {stale.length > 0 ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {t('navData.outOfDate', { units: stale.map((u) => u.unitName).join(', ') })}
          </Alert>
        ) : null}
        {units.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('navData.none')}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {units.map((u) => (
              <Stack key={u.navDataId} direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                <Stack sx={{ flex: 1, minWidth: 160 }}>
                  <Typography variant="subtitle2">
                    {u.unitName}
                    {u.databaseCycle ? ` · ${u.databaseCycle}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('navData.loaded', { date: dateLabel(u.updatedOn) })} · {t('navData.validUntil', { date: dateLabel(u.validUntil) })}
                  </Typography>
                </Stack>
                <NavDataChip state={u.state} />
                {canEdit ? (
                  <>
                    <Button size="small" onClick={() => setEditing(u)} aria-label={t('common.edit')}>
                      <Icon icon="mdi:pencil" />
                    </Button>
                    <Button size="small" color="error" onClick={() => setRemoving(u)} aria-label={t('common.delete')}>
                      <Icon icon="mdi:delete-outline" />
                    </Button>
                  </>
                ) : null}
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
      {editing ? (
        <NavDataDialog registration={registration} unit={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
      <ConfirmDialog
        open={removing !== null}
        title={t('navData.removeTitle')}
        message={t('navData.removeMessage', { unit: removing?.unitName ?? '' })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => removing && remove.mutate(removing)}
        onClose={() => setRemoving(null)}
      />
    </Card>
  )
}

function NavDataDialog({ registration, unit, onClose }: { registration: string; unit: NavDataUnit | null; onClose: () => void }) {
  const { t } = useTranslation()
  const today = useClubToday()
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const [unitName, setUnitName] = useState(unit?.unitName ?? '')
  const [databaseCycle, setCycle] = useState(unit?.databaseCycle ?? '')
  const [updatedOn, setUpdatedOn] = useState(unit?.updatedOn ?? today)
  const [validUntil, setValidUntil] = useState(unit?.validUntil ?? airacCycleOn(today).validUntil)

  // The cycle in force today: what a pilot who just loaded the data wants.
  const applyCurrentCycle = () => {
    const cycle = airacCycleOn(today)
    setCycle(cycle.id)
    setUpdatedOn(today)
    setValidUntil(cycle.validUntil)
  }

  const save = useMutation({
    mutationFn: (body: NavDataInput) => api.post(`/api/maintenance/${registration}/nav-data`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['aircraft'] })
      showSnackbar(t('navData.saved'))
      onClose()
    },
    onError: () => showSnackbar(t('common.error'), { severity: 'error' }),
  })

  const valid = unitName.trim() !== '' && updatedOn !== '' && validUntil >= updatedOn

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{unit ? t('navData.edit') : t('navData.add')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('navData.unitName')}
            value={unitName}
            onChange={(e) => setUnitName(e.target.value)}
            disabled={unit !== null}
            helperText={t('navData.unitHelp')}
            autoComplete="off"
          />
          <Button variant="outlined" startIcon={<Icon icon="mdi:calendar-sync" />} onClick={applyCurrentCycle}>
            {t('navData.currentCycle', { cycle: airacCycleOn(today).id })}
          </Button>
          <TextField label={t('navData.cycle')} value={databaseCycle} onChange={(e) => setCycle(e.target.value)} autoComplete="off" />
          <TextField
            type="date"
            label={t('navData.updatedOn')}
            value={updatedOn}
            onChange={(e) => setUpdatedOn(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date"
            label={t('navData.validUntilLabel')}
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            error={validUntil < updatedOn}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || save.isPending}
          onClick={() => save.mutate({ unitName: unitName.trim(), databaseCycle: databaseCycle.trim() || null, updatedOn, validUntil })}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
